import React from 'react';
import { useNavigation } from '@react-navigation/native';
import { useLayoutSize } from '../../theme';
import WebSidebarLayout from './WebSidebarLayout';
import WebHeaderBarInjector from './WebHeaderBarInjector';
import type { WebHeaderBarProps } from './WebHeaderBar';

type Props = {
  children: React.ReactNode;
  /** Props forwarded to WebHeaderBar. Omit onBack to get default navigation.goBack(). */
  header?: Omit<WebHeaderBarProps, 'onBack'> & { onBack?: () => void };
  isDemoMode?: boolean;
  cartBadgeOverride?: number;
};

/**
 * Convenience wrapper: WebSidebarLayout + automatic WebHeaderBar injection.
 * When sidebar layout is inactive, renders children only (no header bar).
 */
export default function WebScreenShell({
  children,
  header,
  isDemoMode,
  cartBadgeOverride,
}: Props) {
  return (
    <WebSidebarLayout
      isDemoMode={isDemoMode}
      cartBadgeOverride={cartBadgeOverride}>
      {header ? <WebHeaderBarInjectorWithNav {...header} /> : null}
      {children}
    </WebSidebarLayout>
  );
}

function WebHeaderBarInjectorWithNav(
  props: Omit<WebHeaderBarProps, 'onBack'> & { onBack?: () => void },
) {
  const { useSidebarNav } = useLayoutSize();
  const navigation = useNavigation();
  if (!useSidebarNav) return null;

  const onBack =
    props.onBack ??
    (() => {
      try {
        if (navigation.canGoBack()) navigation.goBack();
      } catch {}
    });

  return <WebHeaderBarInjector {...props} onBack={onBack} />;
}
